import { useRef, useState } from "react";
import {
  Popover,
  PopoverTrigger,
  PopoverContent,
  Button,
  Text,
  useDisclosure,
  Flex,
  Box,
  Input,
  useToast,
  ToastId,
  Spinner,
} from "@chakra-ui/react";
import { useGetSavedSetups, useRemoveSavedSetups, useSaveSavedSetups, useTuningForRace } from "hooks";
import { IconCheckGreen, IconChevron, IconPlusCircle, IconWarning } from "icons";
import SavedSetup from "./SavedSetup";
import { RaceTuningInput, SavedSetups as SavedSetupsType } from "api/generated/graphql";
import { RToast } from "components";
import { useQueryClient } from "@tanstack/react-query";
import { Tyres } from "types";
import { useTuning } from "context";

interface SavedSetupsProps {
  carId: string;
  tuningSelections: RaceTuningInput;
  selectedSavedSetup: SavedSetupsType | null;
  setSelectedSavedSetup: (savedSetup: SavedSetupsType | null) => void;
  selectedTyres: Tyres;
  setTuningSelections: (selections: RaceTuningInput) => void;
  setSelectedTyres: (tyres: Tyres) => void;
}

const SavedSetups = ({ 
  carId, 
  tuningSelections,
  selectedSavedSetup,
  setSelectedSavedSetup,
  selectedTyres,
  setTuningSelections,
  setSelectedTyres
}: SavedSetupsProps) => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [showSSInput, setShowSSInput] = useState(false);
  const [setupName, setSetupName] = useState("");
  const [deletingSetupId, setDeletingSetupId] = useState<string | null>(null);
  const [applyingSetupId, setApplyingSetupId] = useState<string | null>(null);
  const toast = useToast();
  const queryClient = useQueryClient();
  const toastIdRef = useRef<ToastId[]>([])

  const {
    setSpeed,
    setSlowTurns,
    setFastTurns,
    setCooling,
    setHandling,
    setBraking,
    setAcceleration,
    setStability,
  } = useTuning();

  const { data: savedSetupsData, isLoading: isLoadingSavedSetupsData } =
  useGetSavedSetups({ carId });
  const { mutateAsync: saveSavedSetups, isLoading: isLoadingSave } = useSaveSavedSetups({
    onSuccess: () => {
      const id = toast({
        position: 'bottom-right',
        render: () => (
          <RToast
            variant="success"
            title={`Setup successfully saved`}
            onClose={() => {
              if (toastIdRef.current.includes(id)) {
                toast.close(id);
                toastIdRef.current = toastIdRef.current.filter(
                  (toastId) => toastId !== id
                );
              }
            }}
          />
        ),
      });
      toastIdRef.current.push(id);
    },
    onError: (error: unknown) => {
      const id = toast({
        position: 'bottom-right',
        render: () => (
          <RToast
            variant="error"
            title={`Error saving setup`}
            description={`${
              error
            }`}
            onClose={() => {
              if (toastIdRef.current.includes(id)) {
                toast.close(id);
                toastIdRef.current = toastIdRef.current.filter(
                  (toastId) => toastId !== id
                );
              }
            }}
          />
        ),
      });
      toastIdRef.current.push(id);
    },
  });
  const { mutateAsync: removeSavedSetups, isLoading: isLoadingRemove } = useRemoveSavedSetups({
    onSuccess: () => {
      const id = toast({
        position: 'bottom-right',
        render: () => (
          <RToast
            variant="success"
            title={`Saved Setup deleted`}
            onClose={() => {
              if (toastIdRef.current.includes(id)) {
                toast.close(id);
                toastIdRef.current = toastIdRef.current.filter(
                  (toastId) => toastId !== id
                );
              }
            }}
          />
        ),
      });
      toastIdRef.current.push(id);
      setSelectedSavedSetup(null);
      setDeletingSetupId(null);
    },
    onError: (error: unknown) => {
      const id = toast({
        position: 'bottom-right',
        render: () => (
          <RToast
            variant="error"
            title={`This setup could not be deleted`}
            description={`${
              error
            }`}
            onClose={() => {
              if (toastIdRef.current.includes(id)) {
                toast.close(id);
                toastIdRef.current = toastIdRef.current.filter(
                  (toastId) => toastId !== id
                );
              }
            }}
          />
        ),
      });
      toastIdRef.current.push(id);
    },
  });

  const { tuningForRace } = useTuningForRace();
  const { mutateAsync: mutateTuning } = tuningForRace;

  const lastUsedSetup = savedSetupsData?.getSavedSetups.find(setup => setup.name === 'Last Used');
  const savedSetups = savedSetupsData?.getSavedSetups.filter(setup => setup.name !== 'Last Used');

  const handleSave = async () => {
    if (!tuningSelections) return;

    const setupParts = {
      speed: tuningSelections?.speed,
      slowTurns: tuningSelections?.slowTurns,
      fastTurns: tuningSelections?.fastTurns,
      cooling: tuningSelections?.cooling,
      handling: tuningSelections?.handling,
      braking: tuningSelections?.braking,
      acceleration: tuningSelections?.acceleration,
      stability: tuningSelections?.stability,
      tyresId: selectedTyres,
    };

    const filteredSetupParts = Object.fromEntries(
      Object.entries(setupParts).filter(([_, value]) => value !== undefined)
    );

    try {
      await saveSavedSetups({
        SavedSetupsInput: {
          name: setupName,
          carId,
          ...filteredSetupParts,
        },
      });
      queryClient.invalidateQueries({ queryKey: ['getSavedSetups'] });
      setSetupName("");
      setShowSSInput(false);
    } catch (error) {
      console.error("Error saving setup:", error);
    }
  };

  const handleRemoveSavedSetup = async (savedSetupId: string) => {
    setDeletingSetupId(savedSetupId);
    await removeSavedSetups({ SavedSetupsInput: { id: savedSetupId, carId } });
    queryClient.invalidateQueries({
      queryKey: ['getSavedSetups'],
    });
  }

  const handleApplySavedSetup = async (savedSetup: SavedSetupsType) => {
    setApplyingSetupId(savedSetup.id as string);
    const setupParts = {
      speed: savedSetup?.speed,
      slowTurns: savedSetup?.slowTurns,
      fastTurns: savedSetup?.fastTurns,
      cooling: savedSetup?.cooling,
      handling: savedSetup?.handling,
      braking: savedSetup?.braking,
      acceleration: savedSetup?.acceleration,
      stability: savedSetup?.stability,
      tyresId: savedSetup?.tyresId,
    };

    const filteredSetupParts = Object.fromEntries(
      Object.entries(setupParts).filter(([_, value]) => value !== undefined)
    );

    try {
      setTuningSelections({
        ...tuningSelections,
        ...filteredSetupParts,
      });
      
      await mutateTuning({
        raceTuningInput: {
          ...filteredSetupParts,
          raceId: tuningSelections.raceId,
          carId: tuningSelections.carId,
        },
      });

      const tuningSetters = {
        speed: setSpeed,
        slowTurns: setSlowTurns,
        fastTurns: setFastTurns,
        cooling: setCooling,
        handling: setHandling,
        braking: setBraking,
        acceleration: setAcceleration,
        stability: setStability,
      };

      Object.entries(tuningSetters).forEach(([key, setter]) => {
        if (savedSetup[key as keyof typeof savedSetup]) {
          setter(savedSetup[key as keyof typeof savedSetup] as number);
        }
      });

      setSelectedTyres(savedSetup?.tyresId as Tyres);
      setApplyingSetupId(null);
      toast({
        position: 'bottom-right',
        render: () => (
          <RToast variant="success" title={`Saved Setup applied`} />
        ),
      });
    } catch (error) {
      console.error('Error applying saved setup:', error);
      setApplyingSetupId(null);
      toast({
        position: 'bottom-right',
        render: () => (
          <RToast variant="error" title={`Error applying saved setup`} />
        ),
      });
    }
  };

  const handleSelectSavedSetup = async (savedSetup: SavedSetupsType) => {
    setSelectedSavedSetup(savedSetup);
    await handleApplySavedSetup(savedSetup);
  };

  return (
    <Box position="relative" zIndex={200}>
      <Popover isOpen={isOpen} onClose={onClose}>
        <PopoverTrigger>
          <Button
            rightIcon={<IconChevron w="12px" h="12px" __css={{ transform: 'rotate(90deg)' }} />}
            variant="secondary-gradient"
            textTransform="none"
            h="1.75rem"
            fontSize="0.875rem"
            px="3"
            onClick={onOpen}
          >
            Saved Setups
          </Button>
        </PopoverTrigger>
        <PopoverContent
            w={{ base: '100%', md: '430px' }}
            border="none"
        >
          <Flex
            background="rgba(21, 21, 23, 1)"
            borderRadius="4px"
            p="2"
            gap="2"
            border="2px solid rgba(255, 255, 255, 0.1)"
            position="relative"
            w="full"
            flexDir="column"
            style={{
              backgroundOrigin: 'border-box',
            }}
          >
            <Box 
              background="linear-gradient(90deg, rgba(215, 215, 215, 0.1) 0%, rgba(215, 215, 215, 0) 50%)"
              borderRadius="4px"
              position="absolute"
              inset={0}
              zIndex="-1"
            />
            {lastUsedSetup && (
              <Flex
                flexDir="column"
                gap="2"
                w="full"
                p="2"
              >
                <Text
                  fontSize={{ base: '12.5px', md: '14.5px' }}
                  lineHeight={{ base: '14px', md: '18px' }}
                  fontWeight={400}
                  background="linear-gradient(225deg, #FFFFFF 0%, #EEEEEE 18%, #A0A0A0 73%, #979797 100%)"
                  backgroundClip="text"
                  fontFamily="heading"
                >
                  LAST RACE
                </Text>
                <SavedSetup
                  isSelected={selectedSavedSetup?.id === lastUsedSetup?.id}
                  savedSetup={lastUsedSetup as SavedSetupsType}
                  lastUsed={true}
                  onDelete={() => null}
                  isLoadingRemove={isLoadingRemove}
                  isApplyingSavedSetup={applyingSetupId === lastUsedSetup?.id}
                  handleSelectSavedSetup={handleSelectSavedSetup}
                />
              </Flex>
            )}

            <Flex
              w="full"
              flexDir="column"
              px="2"
            >
              <Button
                variant="upgrade-button"
                fontSize="0.875rem"
                leftIcon={<IconPlusCircle 
                    __css={{
                        path: {
                            fill: 'rgba(53, 255, 236, 0.8)',
                        }
                    }}
                />}
                gap="0"
                color="rgba(53, 255, 236, 0.8)"
                onClick={() => setShowSSInput(true)}
              >
                Create New Setup
              </Button>

              {showSSInput && (
                <Flex 
                  gap="2"
                  alignItems="center"
                  p="3"
                >
                  <Input 
                    placeholder="Setup Name"
                    border="1.5px solid rgba(255, 255, 255, 0.2)"
                    background="rgba(21, 21, 23, 1)"
                    color="white.80"
                    fontSize="16px"
                    fontWeight={400}
                    lineHeight="120%"
                    borderRadius="4px"
                    minH="43px"
                    _placeholder={{
                      color: "white.40",
                    }}
                    onChange={(e) => setSetupName(e.target.value)}
                  />
                  <Button
                    variant="upgrade-button"
                    textTransform="none"
                    fontSize="0.875rem"
                    px="3"
                    w="fit-content"
                    onClick={handleSave}
                    isLoading={isLoadingSave}
                  >
                    <IconCheckGreen />
                  </Button>
                </Flex>
              )}
            </Flex>
            

            <Flex
              flexDir="column"
              gap="2"
              w="full"
              p="2"
            >
              <Text
                fontSize={{ base: '12.5px', md: '14.5px' }}
                lineHeight={{ base: '14px', md: '18px' }}
                fontWeight={400}
                background="linear-gradient(225deg, #FFFFFF 0%, #EEEEEE 18%, #A0A0A0 73%, #979797 100%)"
                backgroundClip="text"
                fontFamily="heading"
              >
                SAVED SETUPS
              </Text>
              <Flex
                flexDir="column"
                gap="2"
                w="full"
              >
                {isLoadingSavedSetupsData ? (
                  <Flex
                    w="full"
                    justify="center"
                    align="center"
                  >
                    <Spinner />
                  </Flex>
                ) : savedSetups?.length === 0 ? (
                  <Text
                    fontSize="12.5px"
                    lineHeight="14px"
                    fontWeight={400}
                  >
                    No saved setups
                  </Text>
                ) : (
                  savedSetups?.map((setup) => (
                    <SavedSetup
                      key={setup.id}
                      isSelected={selectedSavedSetup?.id === setup.id}
                      savedSetup={setup as SavedSetupsType}
                      lastUsed={false}
                      onDelete={handleRemoveSavedSetup}
                      isLoadingRemove={isLoadingRemove && deletingSetupId === setup.id}
                      handleSelectSavedSetup={handleSelectSavedSetup}
                      isApplyingSavedSetup={applyingSetupId === setup.id}
                    />
                  ))
                )}
              </Flex>
            </Flex>

            <Flex
              w="full"
              justify="center"
              align="center"
              gap="1"
            >
              <IconWarning 
                __css={{
                  path: {
                    fill: 'rgba(255, 255, 255, 0.6)',
                  }
                }}
                width="16px"
                height="16px"
              />
              <Text
                fontSize="12.5px"
                lineHeight="14px"
                fontWeight={500}
                color="white.60"
              >
                Saved Setups only apply to this car
              </Text>
            </Flex>
          </Flex>
        </PopoverContent>
      </Popover>
    </Box>
  );
};

export default SavedSetups;